import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import * as apiClient from '../lib/api.js';
import type { PublicUser } from '../lib/api.js';

interface LoginResult {
  ok: boolean;
  mfaRequired: boolean;
  error?: string;
}

interface AuthContextValue {
  user: PublicUser | null;
  loading: boolean;
  mfaPending: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string, turnstileToken: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(false);

  const refresh = useCallback(async () => {
    const res = await apiClient.me();
    if (res.ok && res.data.user) {
      setUser(res.data.user);
      setMfaPending(res.data.mfaPending ?? false);
    } else {
      setUser(null);
      setMfaPending(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        await refresh();
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string, turnstileToken: string): Promise<LoginResult> => {
      const res = await apiClient.login(email, password, turnstileToken);
      if (!res.ok || !res.data.ok) {
        return { ok: false, mfaRequired: false, error: res.data.error ?? 'invalid_credentials' };
      }
      const mfaRequired = res.data.mfaRequired ?? false;
      setMfaPending(mfaRequired);
      await refresh();
      return { ok: true, mfaRequired };
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await apiClient.logout();
    setUser(null);
    setMfaPending(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, mfaPending, refresh, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
